/**
 * AD → Teams helpers (migration_log_0147, 2026-06). Config that used to live in
 * the AD "Settings" nav (now removed for AD) was repointed to its natural homes:
 *   • AgeCutoffControl      → rendered at the top of Teams ▸ Team Levels.
 *   • SchoolYearStartControl → rendered at the top of Teams ▸ Sport Teams.
 *   • TeamDivisions         → new Teams ▸ Divisions sub-tab (was Settings ▸ Divisions).
 * (Coach assign + coach-policy toggles live in Organizations ▸ Coaches;
 *  Draft Board box colors are edited inline per box on the Draft Board tab.)
 * All persist on existing docs/endpoints; no new collections or backend routes.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Clock, Save, Loader2, CheckCircle2, Layers, Plus, Trash2, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const cx = (isDark, d, l) => (isDark ? d : l);

/* ── Age Cut-off (rendered inside Team Levels) ────────────────────────────── */
export function AgeCutoffControl({ orgId, isDark = true }) {
  const [cutoff, setCutoff] = useState({ age_cutoff_month: null, age_cutoff_day: null });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/api/organizations/${orgId}/age-cutoff`)
      .then(({ data }) => setCutoff({ age_cutoff_month: data.age_cutoff_month ?? null, age_cutoff_day: data.age_cutoff_day ?? null }))
      .catch(() => {});
  }, [orgId]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await axios.put(`${API}/api/organizations/${orgId}/age-cutoff`, {
        age_cutoff_month: parseInt(cutoff.age_cutoff_month, 10),
        age_cutoff_day: parseInt(cutoff.age_cutoff_day, 10),
      });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch { toast.error('Could not save cut-off'); }
    finally { setSaving(false); }
  };

  const label = cx(isDark, 'text-slate-400', 'text-slate-500');
  const input = cx(isDark, 'bg-slate-700/50 border-slate-600 text-white', '');
  const box = cx(isDark, 'bg-slate-800/40 border-slate-700', 'bg-amber-50/40 border-amber-100');

  return (
    <div className={`border rounded-lg p-3 ${box}`} data-testid="age-cutoff-control">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="h-4 w-4 text-amber-500" />
        <span className={`text-sm font-semibold ${cx(isDark, 'text-white', 'text-slate-900')}`}>Age Cut-off Date</span>
      </div>
      <p className={`text-[11px] mb-2 ${label}`}>Org-wide date used to compute a player's age at cut-off for eligibility, Birthdays, rosters and the family directory. Leave unset to skip age checks.</p>
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <Label className="text-[10px]">Month</Label>
          <Input type="number" min="1" max="12" value={cutoff.age_cutoff_month ?? ''} placeholder="—"
            onChange={e => setCutoff(p => ({ ...p, age_cutoff_month: e.target.value }))}
            className={`h-8 w-20 ${input}`} data-testid="org-cutoff-month" />
        </div>
        <div>
          <Label className="text-[10px]">Day</Label>
          <Input type="number" min="1" max="31" value={cutoff.age_cutoff_day ?? ''} placeholder="—"
            onChange={e => setCutoff(p => ({ ...p, age_cutoff_day: e.target.value }))}
            className={`h-8 w-20 ${input}`} data-testid="org-cutoff-day" />
        </div>
        <Button onClick={save} disabled={saving || !cutoff.age_cutoff_month || !cutoff.age_cutoff_day}
          className="h-8 bg-amber-600 hover:bg-amber-700 text-white" data-testid="org-cutoff-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save</>}
        </Button>
        {saved && <span className="text-green-500 text-xs flex items-center gap-1" data-testid="org-cutoff-saved"><CheckCircle2 className="h-4 w-4" />Saved</span>}
        <span className={`text-[11px] ml-1 ${label}`} data-testid="org-cutoff-status">
          {(cutoff.age_cutoff_month && cutoff.age_cutoff_day)
            ? <>Current: <strong>{cutoff.age_cutoff_month}/{cutoff.age_cutoff_day}</strong></>
            : <span className="text-amber-500">Not set</span>}
        </span>
      </div>
    </div>
  );
}

/* ── School-Year Start Month (rendered inside Sport Teams) ────────────────── */
export function SchoolYearStartControl({ orgId, isDark = true }) {
  const [month, setMonth] = useState(8);
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/api/organizations/${orgId}`)
      .then(({ data }) => setMonth((data.settings || {}).school_year_start_month || 8))
      .catch(() => {});
  }, [orgId]);
  const save = async (m) => {
    setMonth(m);
    try { await axios.put(`${API}/api/organizations/${orgId}`, { settings: { school_year_start_month: m } }); toast.success('School-year start updated'); }
    catch { toast.error('Save failed'); }
  };
  const label = cx(isDark, 'text-slate-400', 'text-slate-500');
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="school-year-start-block">
      <CalendarDays className="h-4 w-4 text-blue-500" />
      <span className={`text-sm font-medium ${cx(isDark, 'text-slate-300', 'text-slate-700')}`}>School-Year Start Month</span>
      <span title="This ONE setting decides WHEN the current season flips for EVERY sport (school-year calendar). E.g. August means seasons run Aug–Jul."
        className={`cursor-help text-[10px] rounded-full w-4 h-4 inline-flex items-center justify-center border ${cx(isDark, 'border-slate-500 text-slate-400', 'border-slate-400 text-slate-500')}`}
        data-testid="school-year-start-tooltip">?</span>
      <select value={month} onChange={e => save(parseInt(e.target.value))} data-testid="school-year-start-select"
        className={`h-8 rounded px-2 text-sm border ${cx(isDark, 'bg-slate-700 border-slate-600 text-white', 'bg-white border-slate-300')}`}>
        {MONTHS.map((m, idx) => <option key={idx} value={idx + 1}>{m}</option>)}
      </select>
      <span className={`text-[11px] ${label}`}>Controls the current-season flip; the per-sport start month below is only for spring/fall labeling.</span>
    </div>
  );
}

/* ── Divisions sub-tab ────────────────────────────────────────────────────── */
export function TeamDivisions({ orgId, isDark = true }) {
  const [leagueId, setLeagueId] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/api/organizations/${orgId}`).then(async ({ data }) => {
      const lid = data?.league_id || '';
      setLeagueId(lid);
      if (lid) {
        try { const { data: divs } = await axios.get(`${API}/api/divisions/league/${lid}`); setDivisions(divs || []); }
        catch { setDivisions([]); }
      }
    }).catch(() => {});
  }, [orgId]);

  const del = async (id) => {
    try { await axios.delete(`${API}/api/divisions/${id}`); setDivisions(d => d.filter(x => x.id !== id)); toast.success('Division deleted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const card = cx(isDark, 'bg-slate-800/50 border-slate-700', 'bg-white border-slate-200');
  const title = cx(isDark, 'text-white', 'text-slate-900');
  const label = cx(isDark, 'text-slate-400', 'text-slate-500');

  return (
    <Card className={card} data-testid="team-divisions">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`flex items-center gap-2 text-lg ${title}`}><Layers className="h-5 w-5 text-purple-500" />Division Management</CardTitle>
            <p className={`text-sm mt-1 ${label}`}>Create and manage league divisions, assign organizations</p>
          </div>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => setShowAdd(true)} disabled={!leagueId} data-testid="add-division-btn">
            <Plus className="h-4 w-4 mr-1" />Add Division
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!leagueId ? (
          <p className={`text-sm ${label}`}>This organization is not attached to a league.</p>
        ) : divisions.length === 0 ? (
          <div className={`text-center py-8 ${label}`}>
            <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No divisions configured. Create your first division above.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {divisions.map(div => (
              <div key={div.id} className={`p-4 rounded-lg border ${cx(isDark, 'bg-slate-700/30 border-slate-600', 'bg-slate-50 border-slate-200')}`} data-testid={`division-${div.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`font-medium ${title}`}>{div.name}</span>
                    {div.region && <Badge variant="outline" className="ml-2 text-xs">{div.region}</Badge>}
                    {div.max_teams > 0 && <Badge variant="outline" className="ml-2 text-xs">Max {div.max_teams} teams</Badge>}
                    <Badge className="ml-2 bg-purple-600/20 text-purple-400 text-xs">{(div.org_ids || []).length} orgs</Badge>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => del(div.id)} data-testid={`delete-division-${div.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {div.description && <p className={`text-sm mt-1 ${label}`}>{div.description}</p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : ''}>Add New Division</DialogTitle></DialogHeader>
          <AddDivisionForm isDark={isDark} onClose={() => setShowAdd(false)} onSave={async (div) => {
            try {
              const { data } = await axios.post(`${API}/api/divisions/league/${leagueId}`, div);
              setDivisions(d => [...d, data]); setShowAdd(false); toast.success('Division created');
            } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
          }} />
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AddDivisionForm({ isDark, onSave, onClose }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [region, setRegion] = useState('');
  const [maxTeams, setMaxTeams] = useState(0);
  const input = cx(isDark, 'bg-slate-700 border-slate-600 text-white', '');
  return (
    <div className="space-y-4">
      <div>
        <Label className={isDark ? 'text-slate-300' : ''}>Division Name *</Label>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Eastern Conference" className={input} data-testid="division-name-input" />
      </div>
      <div>
        <Label className={isDark ? 'text-slate-300' : ''}>Description</Label>
        <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description" className={input} data-testid="division-desc-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className={isDark ? 'text-slate-300' : ''}>Region</Label>
          <Input value={region} onChange={e => setRegion(e.target.value)} placeholder="e.g., East, West" className={input} data-testid="division-region-input" />
        </div>
        <div>
          <Label className={isDark ? 'text-slate-300' : ''}>Max Teams (0=unlimited)</Label>
          <Input type="number" min="0" value={maxTeams} onChange={e => setMaxTeams(parseInt(e.target.value) || 0)} className={input} data-testid="division-max-input" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700" disabled={!name.trim()}
          onClick={() => onSave({ name: name.trim(), description, region, max_teams: maxTeams })} data-testid="division-save-btn">Create</Button>
      </div>
    </div>
  );
}
